import { DecimalPipe } from "@angular/common";
import { Component, OnInit, inject, computed, Signal } from "@angular/core";
import { MatDialogClose, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RouterLink, Router } from "@angular/router";
import { take } from "rxjs";
import { PlanTemplatesService } from "../../../services/plan-templates.service";
import { VariablesService } from "../../../services/variables.service";
import { getPendingUpgradeInvoice, getPriceRange, groupCostProviders } from "../../../utils/pricing.utils";
import { RenewalDialogClosePayload } from "../dialog-renewal/dialog-renewal.component";
import { AuthService } from "../../../services/auth.service";
import { AccountDto } from "../../../../api/models/fraud.models";
import { DialogService } from "../../../services/dialog.service";

@Component({
  selector: 'app-dialog-subscription-expired',
	imports: [
		MatDialogClose,
		MatIcon,
    DecimalPipe
	],
  templateUrl: './dialog-subscription-expired.component.html',
  styleUrl: './dialog-subscription-expired.component.scss'
})
export class DialogSubscriptionExpiredComponent implements OnInit {
  private readonly _planTemplatesService = inject(PlanTemplatesService);

  readonly _authService: AuthService = inject(AuthService);
  public readonly matDialogRef = inject(MatDialogRef);
  private readonly _dialogService: DialogService = inject(DialogService);
  private readonly _snackBar: MatSnackBar = inject(MatSnackBar);
  readonly variablesService = inject(VariablesService);
  private readonly _router: Router = inject(Router);

  public readonly activatedPlan = this._authService.effectiveUserPlan;
  
  readonly isDeniedForViewer: Signal<boolean> = computed(() => {
    const user: AccountDto | undefined = this._authService.accountDto();
    if (!user) return false;
    return user.accountType === 'CORPORATE' && user.companyRole === 'VIEWER';
  })
  
  readonly isExchangeAccount: Signal<boolean> = computed(() => {
    const user: AccountDto | undefined = this._authService.accountDto();
    if (!user) return false;
    return Boolean(user.accountType === 'CORPORATE' && user.companyDto?.cluster);
  })
  
  readonly isPoliceAccount: Signal<boolean> = computed(() => {
    const user: AccountDto | undefined = this._authService.accountDto();
    if (!user) return false;
    return user.accountType === 'CORPORATE' && user.role === 'POLICE';
  })

  readonly actualPricePlan = computed(() => {
    const plan = this.activatedPlan();
    if (!plan) {
      return null;
    }
    if (plan.userPlanType === 'ENTERPRISE') {
      return this.activatedPlan();
    }
    return this.planTemplates().find((planTemplate) => planTemplate.type === plan.userPlanType) ?? null;
  });

  readonly priceRange = computed(() => {
    const plan = this.actualPricePlan();
    if (!plan) return '0.00';
    return getPriceRange(groupCostProviders(plan));
  });

  readonly complianceHours = computed(() => {
    const plan = this.actualPricePlan();
    if (!plan) return 0;

    if ('complianceHoursYearly' in plan && typeof plan.complianceHoursYearly === 'number') {
      return plan.complianceHoursYearly;
    }

    if ('complianceHoursGranted' in plan && typeof plan.complianceHoursGranted === 'number') {
      return plan.complianceHoursGranted;
    }

    return 0;
  });

  public readonly planTemplates = this._planTemplatesService.planTemplates;

  ngOnInit(): void {
    this._planTemplatesService.startPlanTemplatesSync();
  }

  reactivatePlan(): void {
    this.matDialogRef.close();
    if (this.isExchangeAccount() || this.isPoliceAccount() || this.isDeniedForViewer()) {
      return;
    }
    const planTemplates = this.planTemplates().filter(p => p.type !== 'BASIC');
    const activatedPlan = this.activatedPlan();
    if (!activatedPlan) return;
    const isChooseTemplate = planTemplates.some(p => p.type === activatedPlan.userPlanType);
    if (!isChooseTemplate) {
      this._router.navigate(['/pricing']);
      return;
    }
    if (activatedPlan.userPlanType === 'ENTERPRISE') {
      this._onTalkToSales();
      return;
    }
    const pendingUpgradeInvoiceUserPlanType = getPendingUpgradeInvoice(this._authService.accountDto());

    if (pendingUpgradeInvoiceUserPlanType && pendingUpgradeInvoiceUserPlanType.userPlan?.userPlanType === activatedPlan.userPlanType) {
      this.openDialogInvoicePlanUpgrade(pendingUpgradeInvoiceUserPlanType.uuid);
      return;
    }
    this._openDialogRenewal();
  }
  
  private openDialogInvoicePlanUpgrade(invoiceUuid: string): void {
    this._dialogService.openInvoicePlanUpgradeDialog(invoiceUuid).afterClosed().subscribe();
  }

  private _onTalkToSales(): void {
    this._dialogService.openTalkToSalesDialog({ planName: 'Enterprise Plan' }).afterClosed().subscribe();
  }

  private _openDialogRenewal(): void {
    this._dialogService.openRenewalDialog().afterClosed().pipe(take(1)).subscribe({
      next: (result: RenewalDialogClosePayload | undefined) => {
        this._authService.refresh();
        this.variablesService.triggerBannersReload();
      },
    });
  }
}
